import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Icon, Row, Section, Seg, Spin, Stack, Toggle } from '../ChatAssistant/compact/kit';
import { C } from '../ChatAssistant/compact/tokens';
import { LanguageFlag } from './LanguageFlag';
import { SideColumnSettings } from './SideColumnSettings';
import { font as f } from '../ChatAssistant/compact/style';
import { useAuth } from '../../context/AuthContext';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../i18n';
import { THEMES, THEME_SWATCH, useTheme, type Theme } from '../../theme/themes';
import { CRM_STYLE_KEY_SET, CRM_TRANSLATE_TARGETS, CRM_UI_SCALE, readCrmStyling, type CrmFlag, type CrmStyling } from '../../crm/crmStyling';
import type { StylingPatch } from '../../theme/userStyling';
import { RabbitHead } from '../../brand/Brand';

function ThemePicker({ value, onChange }: { value: Theme; onChange: (theme: Theme) => void }) {
  const { t } = useTranslation('settings');
  return (
    <div role="radiogroup" aria-label={t('styling.theme')} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))', gap: 8 }}>
      {THEMES.map((theme) => {
        const on = theme === value;
        const swatch = THEME_SWATCH[theme];
        return (
          <button
            key={theme}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(theme)}
            className="cpt-hover"
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 7, padding: 6, borderRadius: 10, cursor: 'pointer',
              background: on ? C.tint16 : C.input, border: `1px solid ${on ? C.tintLine45 : C.border}`,
              boxShadow: on ? 'var(--om-glow)' : 'none',
              color: on ? C.text : C.sub, font: f(500, 12.5), textAlign: 'center',
            }}
          >
            {/* How this theme draws the rabbit, on its own page colour. */}
            <span style={{ height: 58, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', background: swatch.background, border: `1px solid ${swatch.border ?? 'color-mix(in srgb, var(--om-ink) 14%, transparent)'}` }}>
              <RabbitHead width={66} theme={theme} />
            </span>
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingBottom: 2 }}>{t(`styling.themes.${theme}`)}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Each language in its own words, so it can be found whatever the app speaks now. */
const NATIVE_NAME: Record<SupportedLanguage, string> = { en: 'English', uk: 'Солов’їна', ru: 'Русский', es: 'Español' };

/**
 * The one language for the whole app (console and CRM), saved to the person's
 * profile. Large and first on the page: someone who cannot read the current
 * language must still find it.
 */
function LanguagePicker() {
  const { t } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');
  const { language, setLanguage } = useAuth();
  const [saving, setSaving] = useState<SupportedLanguage | null>(null);
  const [failed, setFailed] = useState('');

  const change = async (next: SupportedLanguage) => {
    if (next === language || saving) return;
    setFailed('');
    setSaving(next);
    try {
      await setLanguage(next);
    } catch (err) {
      setFailed(err instanceof Error ? err.message : t('languageFailed'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <Section title={t('styling.language')} hint={t('styling.languageHelp')}>
      <div role="radiogroup" aria-label={t('styling.language')} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        {SUPPORTED_LANGUAGES.map((id) => {
          const on = id === language;
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={saving !== null}
              onClick={() => void change(id)}
              className="cpt-hover"
              style={{
                display: 'flex', alignItems: 'center', gap: 12, minHeight: 64, padding: '10px 16px', borderRadius: 10, textAlign: 'left',
                cursor: saving ? 'default' : 'pointer', background: on ? C.tint16 : C.card, border: `${on ? 2 : 1}px solid ${on ? C.purple : C.border}`,
                opacity: saving && saving !== id ? 0.6 : 1,
              }}
            >
              <LanguageFlag language={id} width={38} />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
                <span style={{ font: f(600, 16), color: C.text }}>{NATIVE_NAME[id]}</span>
                {tCommon(`language.${id}`) !== NATIVE_NAME[id] && <span style={{ font: f(400, 12), color: C.muted }}>{tCommon(`language.${id}`)}</span>}
              </span>
              {saving === id ? <Spin size={14} /> : on ? <Icon name="check-circle" size={18} color={C.purple} /> : null}
            </button>
          );
        })}
      </div>
      {failed && <span style={{ font: f(500, 12), color: C.purplePale }}>{failed}</span>}
    </Section>
  );
}

/**
 * Settings → Styling. The theme and the language are app-wide: they change
 * every console page, the React CRM pages and the embedded CRM at once. The
 * rest are the CRM's own settings, which lived in the CRM's Settings drawer
 * until the console took over its sidebar. Each change is saved at once and
 * applies live.
 */
export function StylingTab() {
  const { t } = useTranslation('settings');
  const [theme] = useTheme();
  const { saveStyling } = useAuth();
  const [s, setS] = useState<CrmStyling>(() => readCrmStyling());
  const [saveFailed, setSaveFailed] = useState(false);

  // Follow changes made elsewhere (the CRM in another tab).
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || CRM_STYLE_KEY_SET.has(event.key)) setS(readCrmStyling());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Applies at once; saved to the person's profile (debounced) so it follows them to any device.
  const save = (patch: StylingPatch) => {
    setSaveFailed(false);
    const done = saveStyling(patch).then((stored) => setSaveFailed(!stored), () => setSaveFailed(true));
    setS(readCrmStyling());
    return done;
  };
  const setFlag = (flag: CrmFlag, on: boolean) => save({ flags: { [flag]: on } });
  const flagRow = (flag: CrmFlag) => (
    <Row label={t(`styling.flags.${flag}.label`)} help={t(`styling.flags.${flag}.help`)}>
      <Toggle on={s.flags[flag]} onChange={(on) => setFlag(flag, on)} title={t(`styling.flags.${flag}.label`)} />
    </Row>
  );
  const scale = s.uiScaleDefault ? 100 : s.uiScale;
  const translateOptions = CRM_TRANSLATE_TARGETS.map((id) => ({ id, label: id.toUpperCase() }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 760, width: '100%' }}>
      <span style={{ font: f(400, 12), color: C.sub }}>{t('styling.intro')}</span>
      {saveFailed && <span style={{ font: f(500, 12), color: C.purplePale }}>{t('styling.saveFailed')}</span>}

      <Stack gap={18}>
        <LanguagePicker />

        <Section title={t('styling.appearance')}>
          <Card>
            <Row stack label={t('styling.theme')} help={t('styling.themeHelp')}>
              <ThemePicker
                value={theme}
                onChange={(next) => void save({ theme: next })}
              />
            </Row>
            <Row stack label={t('styling.scale')} help={t('styling.scaleHelp')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, font: f(500, 12.5), color: C.sub }}>
                  <Toggle on={s.uiScaleDefault} onChange={(on) => save({ uiScaleDefault: on })} title={t('styling.scaleDefault')} />
                  {t('styling.scaleDefault')}
                </span>
                <input
                  type="range"
                  min={CRM_UI_SCALE.min}
                  max={CRM_UI_SCALE.max}
                  step={CRM_UI_SCALE.step}
                  value={scale}
                  disabled={s.uiScaleDefault}
                  aria-label={t('styling.scale')}
                  onChange={(e) => save({ uiScale: Number(e.target.value), uiScaleDefault: false })}
                  style={{ flex: '1 1 160px', minWidth: 120, accentColor: C.purple, opacity: s.uiScaleDefault ? 0.45 : 1, cursor: s.uiScaleDefault ? 'not-allowed' : 'pointer' }}
                />
                <span style={{ width: 42, textAlign: 'right', font: f(600, 12.5), color: C.text }}>{scale}%</span>
              </div>
            </Row>
            <Row label={t('styling.mediaView')} help={t('styling.mediaViewHelp')}>
              <Seg
                options={[
                  { id: 'expanded', label: t('styling.mediaExpanded') },
                  { id: 'compact', label: t('styling.mediaCompact') },
                ]}
                value={s.mediaView}
                onChange={(mediaView) => save({ mediaView })}
              />
            </Row>
            <Row label={t('styling.fanslyMenu')} help={t('styling.fanslyMenuHelp')}>
              <Seg
                options={[
                  { id: 'onlyfans', label: t('styling.fanslyMenuOnlyfans') },
                  { id: 'fansly', label: t('styling.fanslyMenuFansly') },
                ]}
                value={s.fanslyMenu}
                onChange={(fanslyMenu) => save({ fanslyMenu })}
              />
            </Row>
            {flagRow('nyx')}
          </Card>
        </Section>

        <Section title={t('styling.fans')}>
          <Card>
            {flagRow('spentBadge')}
            {flagRow('buyingPower')}
            {flagRow('shadeCreators')}
          </Card>
        </Section>

        <Section title={t('styling.chats')}>
          <Card>
            <Row label={t('styling.unreadStyle')} help={t('styling.unreadStyleHelp')}>
              <Seg
                options={[
                  { id: 'highlight', label: t('styling.unreadHighlight') },
                  { id: 'bold', label: t('styling.unreadBold') },
                ]}
                value={s.unreadStyle}
                onChange={(unreadStyle) => save({ unreadStyle })}
              />
            </Row>
            {flagRow('mediaSalesStats')}
            {flagRow('messagePreviewTooltip')}
          </Card>
        </Section>

        <SideColumnSettings />

        <Section title={t('styling.translator')} hint={t('styling.translatorHint')}>
          <Card>
            <Row stack label={t('styling.composerTranslate')} help={t('styling.composerTranslateHelp')}>
              <div style={{ display: 'flex' }}>
                <Seg size="sm" options={translateOptions} value={s.composerTranslate} onChange={(composerTranslate) => save({ composerTranslate })} />
              </div>
            </Row>
            <Row stack label={t('styling.messageTranslate')} help={t('styling.messageTranslateHelp')}>
              <div style={{ display: 'flex' }}>
                <Seg size="sm" options={translateOptions} value={s.messageTranslate} onChange={(messageTranslate) => save({ messageTranslate })} />
              </div>
            </Row>
          </Card>
        </Section>
      </Stack>
    </div>
  );
}
